
import { ChangeDetectorRef, Component, effect, input, model } from '@angular/core';
import type { FormValueControl } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';

@Component({
    selector: 'mce-image-upload',
    imports: [MatCardModule, MatFormFieldModule, MatButtonModule],
    templateUrl: './mce-image-upload.component.html',
})
export class MceImageUploadComponent implements FormValueControl<string> {

  title = input('Image Upload'); // default value
  maxSize = input<number>(); 
  maxImageWidth = input<number>();
  maxImageHeight = input<number>();

  // Add input for initial image URL (e.g., from storage or default image)
  initialImageUrl = input<string | null>(null);
  readonly value = model('');

  imageSrc: string | null = null;
  errorMessage: string | null = null;

  constructor(private cdr: ChangeDetectorRef) {
    effect(() => {
      this.imageSrc = this.value() || this.initialImageUrl();
    });
  }

  onFileSelected(event: Event): void {

    const file = (event.target as HTMLInputElement)?.files?.[0];

    // Reset error message
    this.errorMessage = null;

    if (file) {
      const maxSize = this.maxSize();
      const maxImageWidth = this.maxImageWidth();
      const maxImageHeight = this.maxImageHeight();

      if (maxSize !== undefined && file.size > maxSize) {
        this.errorMessage = `File size should not exceed ${maxSize} bytes`;
        return;
      }

      if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
        this.errorMessage = 'Only JPEG and PNG files are allowed';
        return;
      }

      const reader = new FileReader();

      reader.onload = (e) => {
        const imageData = reader.result as string;
        const img = new Image();
        img.src = imageData;

        img.onload = () => {
          let width = img.width;
          let height = img.height;

          if ((maxImageWidth !== undefined && width > maxImageWidth) ||
            (maxImageHeight !== undefined && height > maxImageHeight)) {

            if (maxImageWidth !== undefined && width > maxImageWidth) {
              const ratio = maxImageWidth / width;
              width = maxImageWidth;
              height = height * ratio;
            }

            if (maxImageHeight !== undefined && height > maxImageHeight) {
              const ratio = maxImageHeight / height;
              height = maxImageHeight;
              width = width * ratio;
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(img, 0, 0, width, height);

            this.imageSrc = canvas.toDataURL(file.type);
          } else {
            this.imageSrc = imageData;
          }

          this.value.set(this.imageSrc);
          this.cdr.detectChanges();
        };
      };

      reader.onerror = (error) => {
        console.log("File could not be read: ", error);
      };

      reader.readAsDataURL(file);
    }
  }

}
